// React and Router imports
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Third-party library imports
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import Select from "react-select";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";

// Firebase imports
import { auth } from "../../firebase.js";

// Utility imports
import { debounce } from "../../utils/debounceUtils.js";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import { templatesApi } from "../../utils/templatesApi";
import TemplateFieldsDialog from "../shared/templates/TemplateFieldsDialog";

// Styles imports
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/Create.css";
import "../../css/EditDocument.css";
import "../../css/QuillOverrides.css";
import "../../css/CreateDocumentFromTranscription.css";
import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";

const API_BASE_URL = "https://bluebirddocumentationadmin.pythonanywhere.com";

Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);

// Quill Core Imports
const Inline = Quill.import("blots/inline");
const Block = Quill.import("blots/block");

// Custom Inline Code Block
class InlineCode extends Inline {
    static create() {
        const node = super.create();
        node.setAttribute("class", "inline-code");
        return node;
    }
    static formats(node) {
        return true;
    }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote
class MultiLineBlockquote extends Block {
    static create() {
        const node = super.create();
        node.setAttribute("class", "multi-line-blockquote");
        return node;
    }
    static formats(node) {
        return true;
    }
}
MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "multi-line-blockquote";
MultiLineBlockquote.className = "multi-line-blockquote";

// Register Custom Quill Components
Quill.register(MultiLineBlockquote);
Quill.register(InlineCode);

function CreateDocumentFromTranscription() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const quillRef = useRef(null);
    const taskRefProps = useTaskReference(quillRef);

    /**
     * Converts raw HTML into Quill-normalized HTML by running it through
     * a temporary hidden Quill instance. This ensures headings, lists,
     * bold/italic, etc. render correctly in the editor.
     * (Mirrors convertHtmlToQuillDelta from ConfigureSpaceImport.js)
     */
    const convertHtmlToQuillDelta = async (htmlContent) => {
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '800px';
        document.body.appendChild(tempContainer);

        try {
            const tempQuill = new Quill(tempContainer, {
                theme: 'snow',
                modules: {
                    toolbar: false,
                    history: { delay: 1000, maxStack: 100, userOnly: true },
                    imageResize: {
                        parchment: Quill.import("parchment"),
                        modules: ["Resize", "DisplaySize"],
                    },
                    table: false,
                    "table-better": {
                        language: "en_US",
                        menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
                        toolbarTable: true,
                    },
                },
            });

            tempQuill.clipboard.dangerouslyPasteHTML(0, htmlContent);
            await new Promise(resolve => setTimeout(resolve, 100));

            const delta = tempQuill.getContents();
            tempQuill.setContents(delta);
            await new Promise(resolve => setTimeout(resolve, 50));

            return tempQuill.root.innerHTML;
        } catch (error) {
            console.error("Error in Quill conversion:", error);
            return htmlContent;
        } finally {
            if (tempContainer.parentNode) {
                document.body.removeChild(tempContainer);
            }
        }
    };

    // Transcription data
    const [transcriptionData, setTranscriptionData] = useState(null);
    const [loadingTranscription, setLoadingTranscription] = useState(true);
    const [transcriptionError, setTranscriptionError] = useState(null);

    // Document type selection
    const [documentDestination, setDocumentDestination] = useState("personal"); // "personal" or "workspace"

    // Template for the notes (BBD-111). "" keeps the plain summary + transcript.
    const [templateOptions, setTemplateOptions] = useState([]);
    const [selectedTemplateRef, setSelectedTemplateRef] = useState("preset:meeting-notes-recording");
    const [pendingTemplate, setPendingTemplate] = useState(null);
    const [templateBusy, setTemplateBusy] = useState(false);
    const [templateError, setTemplateError] = useState("");
    const [templateAppliedFor, setTemplateAppliedFor] = useState(null);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [userWorkspaces, setUserWorkspaces] = useState([]);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

    // Document type and status states
    const [selectedType, setSelectedType] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [hasSpaces, setHasSpaces] = useState(false);

    // Content and metadata states
    const [content, setContent] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    // Document relationship states
    const [allDocumentation, setAllDocumentation] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState("");
    const [selectedParent, setSelectedParent] = useState("");

    // User and authentication states
    const [uid, setUid] = useState("");
    const [userEmail, setUserEmail] = useState("");

    // Collaboration states
    const [emails, setEmails] = useState([]);
    const [currentEmail, setCurrentEmail] = useState("");

    // UI states
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [error, setError] = useState(null);
    const [showTranscriptPanel, setShowTranscriptPanel] = useState(true);

    // Authentication Effect
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
                setUserEmail(user.email);
            } else {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Fetch transcription data
    useEffect(() => {
        if (uid && jobId) {
            fetchTranscriptionData();
        }
    }, [uid, jobId]);

    // Fetch user workspaces when destination changes
    useEffect(() => {
        if (uid && documentDestination === "workspace") {
            fetchUserWorkspaces();
        }
    }, [uid, documentDestination]);

    // Fetch documentation based on destination
    useEffect(() => {
        if (uid) {
            if (documentDestination === "personal") {
                populatePersonalDocumentation();
            } else if (documentDestination === "workspace" && selectedWorkspace) {
                populateWorkspaceDocumentation(selectedWorkspace.id);
            }
        }
    }, [uid, documentDestination, selectedWorkspace]);

    // Recording-aware templates: the two Bluebird variants plus any custom
    // template (personal, or the chosen workspace's) that uses {{summary}} or
    // {{transcript}}.
    useEffect(() => {
        if (!uid) return;
        let cancelled = false;
        const workspaceId = documentDestination === "workspace" ? selectedWorkspace?.id : null;
        (async () => {
            try {
                const [presets, custom] = await Promise.all([
                    templatesApi.listPresets({ recording: true }),
                    templatesApi.list({ workspaceId: workspaceId || undefined, includeContent: true }),
                ]);
                if (cancelled) return;
                const usesRecording = (t) => /\{\{\s*(summary|transcript|recording_title|recording_date)/.test(t.content || "");
                const options = [
                    ...(presets.presets || []).map(p => ({ ...p, group: "Bluebird" })),
                    ...(custom.templates || []).filter(usesRecording).map(t => ({ ...t, group: workspaceId ? "Workspace" : "My templates" })),
                ];
                setTemplateOptions(options);
            } catch (e) {
                console.error("Failed to load recording templates:", e);
            }
        })();
        return () => { cancelled = true; };
    }, [uid, documentDestination, selectedWorkspace?.id]);

    const applyRecordingTemplate = async (template, values) => {
        setTemplateBusy(true);
        setTemplateError("");
        try {
            const rendered = await templatesApi.render({
                ref: template.ref,
                values,
                workspaceId: documentDestination === "workspace" ? selectedWorkspace?.id : null,
                transcriptionJobId: jobId,
            });
            setPendingTemplate(null);
            if (rendered.doc_type) setSelectedType(rendered.doc_type);
            setName(rendered.name || "");
            setDescription(rendered.description || "");
            try {
                setContent(await convertHtmlToQuillDelta(rendered.content || ""));
            } catch (err) {
                setContent(rendered.content || "");
            }
            setTemplateAppliedFor(template.ref);
        } catch (e) {
            setTemplateError(e.message || "Could not apply the template");
        } finally {
            setTemplateBusy(false);
        }
    };

    // Apply the selected template once the job and the option list are ready,
    // and again whenever the selection changes.
    useEffect(() => {
        if (!transcriptionData || !selectedTemplateRef || templateAppliedFor === selectedTemplateRef) return;
        const template = templateOptions.find(t => t.ref === selectedTemplateRef);
        if (!template) return;
        if (template.fields && template.fields.length) {
            setPendingTemplate(template);
        } else {
            applyRecordingTemplate(template, {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcriptionData, selectedTemplateRef, templateOptions]);

    const fetchTranscriptionData = async () => {
        try {
            setLoadingTranscription(true);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/audio/transcribe/jobs/${jobId}/`,
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                        ...(token && { "auth-token": `Bearer ${token}` }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setTranscriptionData(data);
                
                // Build initial HTML from summary/transcript
                let rawHtml = '';
                if (data.summary) {
                    rawHtml = `${data.summary}<h2>Full Transcript</h2><p>${data.transcript || ''}</p>`;
                } else if (data.transcript) {
                    rawHtml = `<p>${data.transcript}</p>`;
                }

                // Run through Quill normalization for proper formatting
                if (rawHtml) {
                    try {
                        const normalizedHtml = await convertHtmlToQuillDelta(rawHtml);
                        setContent(normalizedHtml);
                    } catch (err) {
                        console.error("Quill normalization failed, using raw HTML:", err);
                        setContent(rawHtml);
                    }
                }
            } else {
                setTranscriptionError("Failed to load transcription data");
            }
        } catch (err) {
            console.error("Error fetching transcription:", err);
            setTranscriptionError("Error loading transcription");
        } finally {
            setLoadingTranscription(false);
        }
    };

    const fetchUserWorkspaces = async () => {
        try {
            setLoadingWorkspaces(true);
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const response = await fetch(
                `${API_BASE_URL}/workspaces/`,
                {
                    headers: {
                        ...(token && { Authorization: `${token}` }),
                        ...(token && { "auth-token": `Bearer ${token}` }),
                        ...(uid && { "requesting-user-uid": uid }),
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                // API returns array directly, not {workspaces: [...]}
                // Also filter out duplicates by id and only include workspaces where user can create docs
                const workspaceArray = Array.isArray(data) ? data : (data.workspaces || []);
                const seen = new Set();
                const workspaceOptions = workspaceArray
                    .filter(ws => {
                        // Only include if user is OWNER, ADMIN, or EDITOR (can create documents)
                        const canCreate = ['OWNER', 'ADMIN', 'EDITOR'].includes(ws.role);
                        // Deduplicate by id
                        if (seen.has(ws.id) || !canCreate) return false;
                        seen.add(ws.id);
                        return true;
                    })
                    .map(ws => ({
                        label: ws.name,
                        value: ws.custom_domain,
                        id: ws.id,
                        role: ws.role
                    }));
                setUserWorkspaces(workspaceOptions);
            }
        } catch (err) {
            console.error("Error fetching workspaces:", err);
        } finally {
            setLoadingWorkspaces(false);
        }
    };

    const populatePersonalDocumentation = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            const headers = token ? { 'Authorization': `${token}`, 'auth-token': `Bearer ${token}` } : {};
            
            const [spaceResponse, pageResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/spaces/author/${uid}/`, { headers }),
                fetch(`${API_BASE_URL}/pages/author/${uid}/`, { headers }),
            ]);

            const spaceData = spaceResponse.status === 200 ? await spaceResponse.json() : { spaces: [] };
            const pageData = pageResponse.status === 200 || pageResponse.status === 404 ? await pageResponse.json() : [];
            
            const spaceOptions = Array.isArray(spaceData.spaces)
                ? spaceData.spaces.map((space) => ({ label: space.name, value: space.id }))
                : [];
            const pageOptions = Array.isArray(pageData.pages)
                ? pageData.pages.map((page) => ({ label: page.page_name, value: page.id }))
                : [];
            
            setAllDocumentation([...spaceOptions, ...pageOptions]);
            setHasSpaces(spaceOptions.length > 0);
        } catch (error) {
            console.error("Error fetching personal documentation:", error);
        }
    };

    const populateWorkspaceDocumentation = async (workspaceId) => {
        try {
            // Clear existing documentation first to prevent showing stale data
            setAllDocumentation([]);
            setHasSpaces(false);
            
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            const headers = token ? { 
                Authorization: `${token}`, 
                "auth-token": `Bearer ${token}`,
                "requesting-user-uid": uid 
            } : {};

            const [spacesResponse, pagesResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/workspaces/${workspaceId}/spaces/`, { headers }),
                fetch(`${API_BASE_URL}/workspaces/${workspaceId}/pages/`, { headers }),
            ]);

            // API returns arrays directly, not {spaces: [...]} or {pages: [...]}
            const spacesData = spacesResponse.ok ? await spacesResponse.json() : [];
            const pagesData = pagesResponse.ok ? await pagesResponse.json() : [];

            // Handle both array format and object format for backwards compatibility
            const spacesArray = Array.isArray(spacesData) ? spacesData : (spacesData.spaces || []);
            const pagesArray = Array.isArray(pagesData) ? pagesData : (pagesData.pages || []);

            const spaceOptions = spacesArray.map(space => ({
                label: space.name,
                value: space.id,
                type: 'space'
            }));
            const pageOptions = pagesArray.map(page => ({
                label: page.page_name,
                value: page.id,
                type: 'page'
            }));

            setAllDocumentation([...spaceOptions, ...pageOptions]);
            setHasSpaces(spaceOptions.length > 0);
        } catch (error) {
            console.error("Error fetching workspace documentation:", error);
        }
    };

    const handleSubmit = async () => {
        // Validation checks
        if (!selectedType) {
            alert("Please select a content type (Space or Page).");
            return;
        }

        if (!name || name.trim() === "") {
            alert("Please enter a name for your document.");
            return;
        }

        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(name)) {
            alert("Document name must contain only letters, numbers, spaces, periods, dashes, and underscores.");
            return;
        }

        if (documentDestination === "workspace" && !selectedWorkspace) {
            alert("Please select a workspace.");
            return;
        }

        if (selectedType === "Page" && !selectedParent?.value) {
            alert("Please select a parent for the page.");
            return;
        }

        setIsLoading(true);
        setLoadingMessage('Creating document...');

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            let response;
            let endpoint;
            let body;

            if (documentDestination === "personal") {
                endpoint = `${API_BASE_URL}/documentation/create/`;
                body = {
                    type: selectedType,
                    isPublic: isPublic,
                    emails: emails,
                    content: content,
                    name: name,
                    author: uid,
                    completed: completed,
                    description: description,
                    space: selectedSpace,
                    parentPage: selectedParent,
                };
            } else {
                // Workspace document creation - use the correct endpoint based on type
                const workspaceId = selectedWorkspace.id;
                if (selectedType === "Space") {
                    endpoint = `${API_BASE_URL}/workspaces/${workspaceId}/spaces/create/`;
                    body = {
                        name: name,
                        description: description,
                        is_public: isPublic,
                        content: content,
                        completed: completed,
                    };
                } else {
                    endpoint = `${API_BASE_URL}/workspaces/${workspaceId}/pages/create/`;
                    // Determine if parent is a space or a page
                    const isParentSpace = selectedParent?.type === 'space';
                    body = {
                        page_name: name,
                        description: description,
                        is_public: isPublic,
                        content: content,
                        completed: completed,
                        ...(isParentSpace 
                            ? { space_id: selectedParent?.value } 
                            : { parent_page_id: selectedParent?.value }),
                    };
                }
            }

            response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`,
                    "auth-token": `Bearer ${token}`,
                    "requesting-user-uid": uid,
                },
                body: JSON.stringify(body),
            });

            if (response.ok) {
                const responseData = await response.json();
                setLoadingMessage('Document created successfully! Redirecting...');
                if (documentDestination === "personal") {
                    navigate(`/documentation/${selectedType.toLowerCase()}/${name}/${uid}/`);
                } else {
                    // Use the ID from response for workspace documents
                    const docId = selectedType === "Space" ? responseData.space_id : responseData.page_id;
                    navigate(`/workspace/${selectedWorkspace.value}/${selectedType.toLowerCase()}/${docId}`);
                }
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || errorData.error || "Failed to create document.");
            }
        } catch (error) {
            console.error("Error creating document:", error);
            alert(error.message || "An error occurred while creating the document.");
            setIsLoading(false);
        }
    };

    const handleToggle = (e) => setIsPublic(e.target.checked);
    const handleCompletedToggle = (e) => setCompleted(e.target.checked);
    const handleSelectedType = (e) => setSelectedType(e.target.value);

    const handleAddEmail = () => {
        if (currentEmail && !emails.some((item) => item.email === currentEmail)) {
            setEmails([...emails, { email: currentEmail, permission: "Can Edit" }]);
            setCurrentEmail("");
        } else {
            alert("This email has already been added.");
        }
    };

    const handleRemoveEmail = (index) => {
        const updatedEmails = [...emails];
        updatedEmails.splice(index, 1);
        setEmails(updatedEmails);
    };

    const handlePermissionChange = (index, newPermission) => {
        const updatedEmails = [...emails];
        updatedEmails[index].permission = newPermission;
        setEmails(updatedEmails);
    };

    const insertTranscriptToEditor = async () => {
        if (transcriptionData?.transcript) {
            const rawHtml = `<h2>Full Transcript</h2><p>${transcriptionData.transcript}</p>`;
            try {
                const normalizedHtml = await convertHtmlToQuillDelta(rawHtml);
                setContent(prevContent => `${prevContent}${normalizedHtml}`);
            } catch (err) {
                setContent(prevContent => `${prevContent}${rawHtml}`);
            }
        }
    };

    const insertSummaryToEditor = async () => {
        if (transcriptionData?.summary) {
            const rawHtml = `<h2>AI Summary</h2>${transcriptionData.summary}`;
            try {
                const normalizedHtml = await convertHtmlToQuillDelta(rawHtml);
                setContent(prevContent => `${prevContent}${normalizedHtml}`);
            } catch (err) {
                setContent(prevContent => `${prevContent}${rawHtml}`);
            }
        }
    };

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [FONT_TOOLBAR_ENTRY],
                    [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
                    [{ size: ["small", false, "large"] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ script: "sub" }, { script: "super" }],
                    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
                    [{ indent: "-1" }, { indent: "+1" }],
                    [{ align: [] }],
                    ["blockquote", "code-block"],
                    ["link", "image", "video"],
                    ["inline-code"],
                    ["formula"],
                    ["table-better"],
                    ["clean"],
                    ["undo", "redo"],
                ],
                handlers: {
                    undo: function() { this.quill.history.undo(); },
                    redo: function() { this.quill.history.redo(); }
                }
            },
            history: { delay: 1000, maxStack: 100, userOnly: true },
            imageResize: {
                parchment: Quill.import("parchment"),
                modules: ["Resize", "DisplaySize"],
            },
            table: false,
            "table-better": {
                language: "en_US",
                menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
                toolbarTable: true,
            },
            keyboard: { bindings: QuillTableBetter.keyboardBindings },
        }),
        []
    );

    const maxLengths = { Space: 100, Page: 150 };
    const currentMaxLength = maxLengths[selectedType] || 100;

    // Loading state
    if (loadingTranscription) {
        return (
            <div className="CreateDocumentFromTranscription__loading">
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p>Loading transcription data...</p>
            </div>
        );
    }

    // Error state
    if (transcriptionError) {
        return (
            <div className="CreateDocumentFromTranscription__error-page">
                <i className="fas fa-exclamation-triangle"></i>
                <h2>Error Loading Transcription</h2>
                <p>{transcriptionError}</p>
                <button onClick={() => navigate("/audio-transcription")}>
                    Back to Audio Transcription
                </button>
            </div>
        );
    }

    // Document creation loading
    if (isLoading) {
        return (
            <div className="CreateDocumentFromTranscription__loading">
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p>{loadingMessage}</p>
            </div>
        );
    }

    return (
        <div className="CreateDocumentFromTranscription__div DocEditor">
            {/* Transcript Panel */}
            <div className={`CreateDocumentFromTranscription__transcript-panel ${showTranscriptPanel ? '' : 'CreateDocumentFromTranscription__transcript-panel--collapsed'}`}>
                <div className="CreateDocumentFromTranscription__transcript-header">
                    <h3>
                        <i className="fas fa-file-audio"></i> Transcription Reference
                    </h3>
                    <button 
                        className="CreateDocumentFromTranscription__toggle-panel"
                        onClick={() => setShowTranscriptPanel(!showTranscriptPanel)}
                    >
                        <i className={`fas fa-chevron-${showTranscriptPanel ? 'left' : 'right'}`}></i>
                    </button>
                </div>
                
                {showTranscriptPanel && (
                    <div className="CreateDocumentFromTranscription__transcript-content">
                        {transcriptionData?.summary && (
                            <div className="CreateDocumentFromTranscription__section">
                                <div className="CreateDocumentFromTranscription__section-header">
                                    <h4><i className="fas fa-brain"></i> AI Summary</h4>
                                    <button 
                                        className="CreateDocumentFromTranscription__insert-button"
                                        onClick={insertSummaryToEditor}
                                        title="Insert summary into editor"
                                    >
                                        <i className="fas fa-plus"></i> Insert
                                    </button>
                                </div>
                                <div className="CreateDocumentFromTranscription__text-box">
                                    {transcriptionData.summary}
                                </div>
                            </div>
                        )}

                        {transcriptionData?.transcript && (
                            <div className="CreateDocumentFromTranscription__section">
                                <div className="CreateDocumentFromTranscription__section-header">
                                    <h4><i className="fas fa-file-alt"></i> Full Transcript</h4>
                                    <button 
                                        className="CreateDocumentFromTranscription__insert-button"
                                        onClick={insertTranscriptToEditor}
                                        title="Insert transcript into editor"
                                    >
                                        <i className="fas fa-plus"></i> Insert
                                    </button>
                                </div>
                                <div className="CreateDocumentFromTranscription__text-box CreateDocumentFromTranscription__text-box--scrollable">
                                    {transcriptionData.transcript}
                                </div>
                            </div>
                        )}

                        <div className="CreateDocumentFromTranscription__file-info">
                            <p><strong>Original File:</strong> {transcriptionData?.original_filename}</p>
                            {transcriptionData?.summarization_instructions && (
                                <p><strong>Summarization Instructions:</strong> {transcriptionData.summarization_instructions}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="CreateDocumentFromTranscription__main-content">
                <button 
                    className="CreateDocumentFromTranscription__back-button"
                    onClick={() => navigate("/audio-transcription")}
                >
                    <i className="fas fa-arrow-left"></i> Back to Transcriptions
                </button>
                <h2>Create Document from Transcription</h2>

                {/* Template choice (BBD-111) */}
                <div className="TplStart__row">
                    <label htmlFor="transcription-template" style={{ fontWeight: 600 }}>Template</label>
                    <select
                        id="transcription-template"
                        className="Create__input"
                        style={{ maxWidth: 360 }}
                        value={selectedTemplateRef}
                        disabled={templateBusy}
                        onChange={(e) => {
                            const ref = e.target.value;
                            setSelectedTemplateRef(ref);
                            setTemplateAppliedFor(null);
                            if (!ref && transcriptionData) {
                                const raw = transcriptionData.summary
                                    ? `${transcriptionData.summary}<h2>Full Transcript</h2><p>${transcriptionData.transcript || ''}</p>`
                                    : `<p>${transcriptionData.transcript || ''}</p>`;
                                convertHtmlToQuillDelta(raw).then(setContent).catch(() => setContent(raw));
                            }
                        }}
                    >
                        <option value="">No template (summary and transcript)</option>
                        {["Bluebird", "My templates", "Workspace"].map(group => {
                            const items = templateOptions.filter(t => t.group === group);
                            return items.length ? (
                                <optgroup key={group} label={group}>
                                    {items.map(t => <option key={t.ref} value={t.ref}>{t.name}</option>)}
                                </optgroup>
                            ) : null;
                        })}
                    </select>
                    {templateBusy && <span className="TplStart__note">Filling in the template…</span>}
                    {selectedTemplateRef && transcriptionData && !transcriptionData.summary && !templateBusy && (
                        <span className="TplStart__note">No summary is available for this recording; the summary section stays as a prompt.</span>
                    )}
                    {templateError && <span className="TplStart__error">{templateError}</span>}
                </div>
                {pendingTemplate && (
                    <TemplateFieldsDialog
                        template={pendingTemplate}
                        workspaceId={documentDestination === "workspace" ? selectedWorkspace?.id : null}
                        submitLabel="Fill in notes"
                        busy={templateBusy}
                        error={templateError}
                        onSubmit={(values) => applyRecordingTemplate(pendingTemplate, values)}
                        onCancel={() => { setPendingTemplate(null); setSelectedTemplateRef(""); setTemplateAppliedFor(""); }}
                    />
                )}

                {/* Required Fields Section */}
                <div className="Create__required-fields-div">
                    {/* Destination Selection */}
                    <div className="Create__form-group Create__required-field-margin-top" style={{ flexDirection: "column" }}>
                        <label className="Create__required-field" style={{ marginBottom: "10px" }}>Save To:</label>
                        <div className="CreateDocumentFromTranscription__destination-options">
                            <label className={`CreateDocumentFromTranscription__destination-option ${documentDestination === 'personal' ? 'CreateDocumentFromTranscription__destination-option--selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="destination"
                                    value="personal"
                                    checked={documentDestination === "personal"}
                                    onChange={(e) => {
                                        setDocumentDestination(e.target.value);
                                        setSelectedWorkspace(null);
                                        setAllDocumentation([]);
                                    }}
                                />
                                <div className="CreateDocumentFromTranscription__destination-content">
                                    <i className="fas fa-user"></i>
                                    <div>
                                        <strong>Personal Document</strong>
                                        <p>Save to your personal documentation</p>
                                    </div>
                                </div>
                            </label>

                            <label className={`CreateDocumentFromTranscription__destination-option ${documentDestination === 'workspace' ? 'CreateDocumentFromTranscription__destination-option--selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="destination"
                                    value="workspace"
                                    checked={documentDestination === "workspace"}
                                    onChange={(e) => {
                                        setDocumentDestination(e.target.value);
                                        setAllDocumentation([]);
                                    }}
                                />
                                <div className="CreateDocumentFromTranscription__destination-content">
                                    <i className="fas fa-users"></i>
                                    <div>
                                        <strong>Team Workspace</strong>
                                        <p>Save to a shared team workspace</p>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Workspace Selection */}
                    {documentDestination === "workspace" && (
                        <div className="Create__form-group">
                            <label className="Create__required-field" style={{ paddingTop: "3px" }}>Select Workspace:</label>
                            {loadingWorkspaces ? (
                                <p style={{ color: 'var(--bb-shell-text)' }}><i className="fas fa-spinner fa-spin"></i> Loading workspaces...</p>
                            ) : userWorkspaces.length === 0 ? (
                                <p className="CreateDocumentFromTranscription__no-workspaces">
                                    You don't have any workspaces yet. 
                                    <a href="/workspaces/create"> Create one</a>
                                </p>
                            ) : (
                                <Select
                                    options={userWorkspaces}
                                    value={selectedWorkspace}
                                    onChange={(ws) => {
                                        setSelectedWorkspace(ws);
                                        setAllDocumentation([]);
                                        setSelectedParent(null);
                                        setHasSpaces(false);
                                    }}
                                    placeholder="Choose a workspace..."
                                    isSearchable={true}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            )}
                        </div>
                    )}

                    {/* Content Type */}
                    <div className="Create__form-group">
                        <select
                            className="form-select Create__input Create__required-field-select"
                            aria-label="Content type selection"
                            name="selected_type"
                            value={selectedType}
                            onChange={handleSelectedType}
                            required
                        >
                            <option value="">- Select Content Type -</option>
                            <option value="Space">Space</option>
                            <option value="Page" disabled={!hasSpaces}>Page</option>
                        </select>
                    </div>

                    {selectedType === "Page" && (
                        <div className="Create__form-group">
                            <label className="Create__required-field" style={{ paddingTop: "3px" }}>
                                Select a parent space or page:{" "}
                            </label>
                            <Select
                                options={allDocumentation}
                                value={allDocumentation.find(option => option.value === selectedParent?.value)}
                                onChange={(option) => setSelectedParent(option)}
                                placeholder="Select a parent..."
                                isSearchable={true}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    )}

                    {/* Name */}
                    <div className="Create__form-group">
                        <label className="Create__required-field">Name: </label>
                        <input
                            type="text"
                            id="name"
                            className="Create__input-short-field Create__required-field-select"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={currentMaxLength}
                            placeholder={`Max ${currentMaxLength} characters`}
                            required
                        />
                    </div>
                </div>

                {/* Description */}
                <div className="Create__form-group">
                    <label>Short Description: </label>
                    <input
                        type="text"
                        id="description"
                        className="Create__input-full-field"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Brief description of this document"
                    />
                </div>
                <div className="Create__form-group-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        This description is what invited users will see about this space/page.
                    </p>
                </div>

                {/* Make Public Toggle */}
                <div className="Create__form-group">
                    <label>Make this content public?</label>
                    <label className="Create__switch">
                        <input type="checkbox" checked={isPublic} onChange={handleToggle} />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                <div className="Create__form-group-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        Public content is viewable by anyone that has this document's URL.
                    </p>
                </div>

                {/* Publish Toggle */}
                <div className="Create__form-group">
                    <label>Publish this page upon submitting?</label>
                    <label className="Create__switch">
                        <input type="checkbox" checked={completed} onChange={handleCompletedToggle} />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                <div className="Create__form-group-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        Whether the document is public or not, this document will not be{" "}
                        <strong>viewable</strong> by anyone until published. Collaborators that have "Can Edit"
                        permission can edit this document.
                    </p>
                </div>

                {/* Collaboration - Personal docs only */}
                {documentDestination === "personal" && (
                    <div className="Create__form-group" style={{ flexDirection: "column", marginBottom: "60px" }}>
                        <label>Invite others to collaborate on this content.</label>
                        {emails.map((item, index) => (
                            <div
                                key={index}
                                className="Create__email-item"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                }}
                            >
                                <span>{item.email}</span>
                                <select
                                    value={item.permission}
                                    className="Create__select"
                                    onChange={(e) => handlePermissionChange(index, e.target.value)}
                                    style={{ marginLeft: "10px", padding: "5px" }}
                                >
                                    <option value="Can Edit">Can Edit</option>
                                    <option value="Can View">Can View</option>
                                </select>
                                <button
                                    onClick={() => handleRemoveEmail(index)}
                                    style={{
                                        marginLeft: "10px",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    <i className="fas fa-trash-alt" title="Remove Email" style={{ color: "white" }}></i>
                                </button>
                            </div>
                        ))}
                        <div className="Create__email-input-container">
                            <input
                                type="email"
                                className="Create__email-input"
                                placeholder="Enter E-mail Address"
                                value={currentEmail}
                                onChange={(e) => setCurrentEmail(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                            />
                            <button onClick={handleAddEmail} className="Create__add-email-btn">
                                <i className="fas fa-plus"></i>
                            </button>
                        </div>
                        <div className="Create__form-group-helper-text">
                            <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                            <p className="Create__helper-text">
                                As of this current version, any added permissions will{" "}
                                <strong>not</strong> automatically be added to any future child
                                documents that are created.
                            </p>
                        </div>
                    </div>
                )}

                {/* Editor */}
                <div className="EditDocument__edit-document-container">
                    <div className="EditDocument__editor-section">
                        <div className="EditDocument__quill-container">
                            <div className="EditDocument__quill-toolbar"></div>
                            <div className="QuillOverrides__content-formatting">
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={content}
                                    onChange={setContent}
                                    modules={modules}
                                    placeholder="Edit your document content here..."
                                    style={{ height: "70vh" }}
                                />
                                <TaskReferenceDropdown {...taskRefProps} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="EditDocument__button-container">
                    <button 
                        className="CreateDocumentFromTranscription__cancel-button"
                        onClick={() => navigate("/audio-transcription")}
                    >
                        Cancel
                    </button>
                    <button 
                        className="submit-button"
                        onClick={handleSubmit}
                        disabled={!name || !selectedType || (documentDestination === "workspace" && !selectedWorkspace)}
                    >
                        <i className="fas fa-save"></i> Create Document
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateDocumentFromTranscription;
